import { useEffect, useRef, useState } from 'react';
import { Bookmark, Trash2, X } from 'lucide-react';
import { Button } from './button';
import { api } from '../../services/api';
import {
  presetDetail, presetSummary, settingsKey, activeFilters,
  type FilterPreset, type FilterPresetPage, type FilterSettings,
} from '../../utils/filterPresets';

// "Saved" button for menus with a filter row: save the current filters, sort and view under a name, pick one saved
// earlier, or one of the last settings used — recorded by itself a few seconds after the filters stop changing, so
// yesterday's filters are there even when nobody saved them. Stored per account (backend/src/routes/filter-presets.ts).

const RECORD_AFTER_MS = 4000;
const when = (iso: string) =>
  new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function FilterPresets({ page, current, onApply, label }: {
  page: FilterPresetPage;
  current: FilterSettings;                 // what the menu shows now
  onApply: (s: FilterSettings) => void;    // put a preset back into the menu's state (and refetch if it must)
  label?: (key: string) => string;         // column key → header name, for the descriptions
}) {
  const [open, setOpen] = useState(false);
  const [named, setNamed] = useState<FilterPreset[]>([]);
  const [recent, setRecent] = useState<FilterPreset[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState('');
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(open);
  openRef.current = open;

  const load = async () => {
    setLoading(true);
    const r = await api.getFilterPresets(page);
    setLoading(false);
    if (r.success && r.data) {
      setNamed(r.data.named);
      setRecent(r.data.recent);
      setError(null);
    } else setError(r.error || 'Could not load presets');
  };

  // Record the settings in use once they stop changing: never the state the menu opened with (also what Reset goes
  // back to), never one without filters and sort
  const key = settingsKey(current);
  const lastKey = useRef<string | null>(null);
  const firstKey = useRef<string | null>(null);
  useEffect(() => {
    if (lastKey.current === null) {
      lastKey.current = firstKey.current = key;
      return;
    }
    if (key === lastKey.current || key === firstKey.current || (!activeFilters(current).length && !current.sort)) return;
    const t = setTimeout(() => {
      lastKey.current = key;
      api.recordFilterRecent(page, current).then(r => { if (r.success && openRef.current) load(); });
    }, RECORD_AFTER_MS);
    return () => clearTimeout(t);
  }, [key]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Close on a click outside or Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const toggle = () => {
    if (!open) { setConfirmDel(null); load(); }
    setOpen(o => !o);
  };
  const apply = (p: FilterPreset) => { onApply(p.settings); setOpen(false); };
  const existing = named.find(p => (p.name ?? '').toLowerCase() === name.trim().toLowerCase());
  const save = async () => {
    const n = name.trim();
    if (!n) return;
    const r = await api.saveFilterPreset(page, n, current);
    if (!r.success) { setError(r.error || 'Could not save the preset'); return; }
    setName('');
    load();
  };
  const remove = async (id: string) => {
    const r = await api.deleteFilterPreset(id);
    setConfirmDel(null);
    if (!r.success) { setError(r.error || 'Could not delete the preset'); return; }
    load();
  };

  const Item = ({ p }: { p: FilterPreset }) => (
    <div className="group flex items-center gap-1 rounded px-1.5 h-7 hover:bg-white/5">
      <button type="button" onClick={() => apply(p)} className="flex flex-1 min-w-0 items-baseline gap-2 text-left"
              title={`${p.kind === 'named' ? `${p.name} · ` : ''}${when(p.updated_at)}\n${presetDetail(p.settings, label)}`}>
        <span className="text-[12px] text-foreground truncate">{p.kind === 'named' ? p.name : when(p.updated_at)}</span>
        <span className="ml-auto shrink-0 text-[11px] text-muted-foreground">{presetSummary(p.settings, label)}</span>
      </button>
      {p.kind === 'named' ? (
        confirmDel === p.id ? (
          <span className="flex items-center gap-1.5 text-[11px] shrink-0">
            <button type="button" className="text-[#ff5c5c] hover:underline" onClick={() => remove(p.id)}>Delete</button>
            <button type="button" className="text-muted-foreground hover:underline" onClick={() => setConfirmDel(null)}>Cancel</button>
          </span>
        ) : (
          <button type="button" aria-label={`Delete ${p.name}`} onClick={() => setConfirmDel(p.id)}
                  className="p-0.5 text-muted-foreground opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-[#ff5c5c] shrink-0">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )
      ) : null}
    </div>
  );

  return (
    <div ref={boxRef} className="relative">
      <Button type="button" onClick={toggle} variant="outline" size="sm" className="flex items-center gap-2 h-8"
              aria-expanded={open} title="Save the filters, or pick saved ones">
        <Bookmark className="w-4 h-4" />
        Saved
      </Button>
      {open ? (
        <div className="absolute right-0 top-full mt-1 z-50 w-72 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto rounded-lg border border-[#3a4252] bg-card shadow-xl p-1.5 text-sm">
          <div className="flex items-center gap-2 px-1.5">
            <span className="text-[11px] text-muted-foreground truncate" title={presetDetail(current, label)}>Now: {presetSummary(current, label)}</span>
            <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="ml-auto p-0.5 text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <form className="mt-1.5 flex gap-1 px-1" onSubmit={e => { e.preventDefault(); save(); }}>
            <input value={name} onChange={e => setName(e.target.value)} maxLength={60} placeholder="New preset name…"
                   title={existing ? `"${existing.name}" will be replaced with the current filters` : undefined}
                   className="flex-1 min-w-0 h-7 px-2 rounded-md border border-[#3a4252] bg-background text-[12px] focus:outline-none focus:border-primary/50" />
            <Button type="submit" size="sm" className={`h-7 px-2.5 text-[12px] ${existing ? 'bg-[#ffa500] hover:bg-[#ffa500]/90 text-black' : ''}`}
                    disabled={!name.trim()} title={existing ? `Replace "${existing.name}"` : undefined}>
              {existing ? 'Replace' : 'Save'}
            </Button>
          </form>
          {error ? <div className="mt-1 px-1.5 text-[11px] text-[#ffa500]">{error}</div> : null}

          <div className="mt-2 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Saved</div>
          {named.length ? named.map(p => <Item key={p.id} p={p} />)
            : <div className="px-1.5 h-6 flex items-center text-[11px] text-muted-foreground">{loading ? 'Loading…' : 'None yet'}</div>}

          <div className="mt-1.5 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
               title="Recorded automatically a few seconds after the filters stop changing (last 10)">Recent</div>
          {recent.length ? recent.map(p => <Item key={p.id} p={p} />)
            : <div className="px-1.5 h-6 flex items-center text-[11px] text-muted-foreground">{loading ? 'Loading…' : 'None yet, recorded automatically as you filter'}</div>}
        </div>
      ) : null}
    </div>
  );
}
